import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useFeatureGate } from '../../hooks/useFeatureGate'
import { useRole } from '../../hooks/auth/useRole'

export interface PeopleFlowTab {
  title: string
  path: string
  tab: string
}

/**
 * The two halves of the employee lifecycle, shown as the Flows page's main tabs.
 * Existing inflow/outflow URLs remain canonical so bookmarks, emails, and API
 * cache keys keep working; this only unifies how people navigate them.
 */
export const usePeopleFlowTabs = (): PeopleFlowTab[] => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isOrgAdmin, isSuperAdmin } = useRole()
  const { hasFeature } = useFeatureGate()

  const isHiring = location.pathname.startsWith('/user/inflow')
  const isDeparture = location.pathname.startsWith('/user/outflow')
  const canSeeHiring = isSuperAdmin || hasFeature('inflow') || isHiring
  const canSeeDepartures =
    (isOrgAdmin || isSuperAdmin) && (isSuperAdmin || hasFeature('outflow') || isDeparture)

  return [
    ...(canSeeHiring
      ? [{ tab: 'hiring', title: t('peopleFlow.hiring.title', 'Hiring & onboarding'), path: '/user/inflow/candidates' }]
      : []),
    ...(canSeeDepartures
      ? [{ tab: 'departures', title: t('peopleFlow.departures.title', 'Departures'), path: '/user/outflow/offboarding' }]
      : []),
  ]
}

export interface PeopleFlowSubTabsProps {
  /** The tabs of the current flow (Candidates, Pipeline, ... or Offboarding, ...). */
  subTabs?: { title: string; path?: string; tab: string }[] | null
  onSelectSubTab?: (tab: string, path?: string) => void
}
